import { BookOpen, Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  categoryLabel,
  severityLabel,
  supportArticleUrl,
} from '@/lib/siteHealth'
import { useAnalytics, useDocsBaseUrl } from '@/provider/useAuth'
import { explanationParagraphs, formatActionType } from './SiteHealthFormat'
import { EYEBROW_CLASS, severityBadgeClass } from './SiteHealthStyles'
import type { FindingTypeInfoDialogProps } from './siteHealthComponents.types'

export default function FindingTypeInfoDialog({
  report,
}: FindingTypeInfoDialogProps) {
  const analytics = useAnalytics()
  const docsBaseUrl = useDocsBaseUrl()
  const articleUrl = supportArticleUrl(docsBaseUrl, report.supportArticleSlug)
  const steps = report.fixSteps ?? []
  const paragraphs = explanationParagraphs(report.explanation)

  return (
    <Dialog
      onOpenChange={(open) => {
        if (open) {
          analytics.track('site_health_guidance_opened', {
            findingType: report.typeKey,
            source: 'info',
          })
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="What this means"
          className="size-7 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
        >
          <Info className="size-4" aria-hidden="true" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] gap-6 overflow-y-auto p-8 sm:max-w-3xl">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <span className={severityBadgeClass(report.severity)}>
              {severityLabel(report.severity)}
            </span>
            <span className="text-xs text-muted-foreground">
              {categoryLabel(report.category)}
            </span>
          </div>
          <DialogTitle className="mt-1">{report.title}</DialogTitle>
          {report.description && (
            <DialogDescription>{report.description}</DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-6">
          {paragraphs.length > 0 && (
            <section>
              <h3 className={EYEBROW_CLASS}>Why it matters</h3>
              <div className="mt-2 space-y-3 text-sm leading-6 text-foreground">
                {paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </section>
          )}

          <section>
            <h3 className={EYEBROW_CLASS}>
              How to fix · {formatActionType(report.actionType)}
            </h3>
            {steps.length > 0 ? (
              <ol className="mt-3 space-y-3">
                {steps.map((step, index) => (
                  <li
                    key={`${index + 1}-${step.title}`}
                    className="grid grid-cols-[auto_1fr] gap-3"
                  >
                    <span className="flex size-6 items-center justify-center rounded-full bg-muted text-xs tabular-nums text-muted-foreground ring-1 ring-border">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium leading-6">{step.title}</p>
                      <p className="text-sm leading-6 text-muted-foreground">
                        {step.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                Structured fix steps are not available for this finding type.
              </p>
            )}
          </section>

          {articleUrl && (
            <a
              href={articleUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() =>
                analytics.track('site_health_support_article_opened', {
                  findingType: report.typeKey,
                  supportArticleSlug: report.supportArticleSlug,
                })
              }
              className="inline-flex items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              <BookOpen className="size-4" aria-hidden="true" />
              Read the full guide
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
